import type { FC, ReactNode } from 'react'
import type { MessageTemplate } from '@/lib/clinic/types'
import type { TemplateValues } from '@/lib/clinic/whatsapp'

import { useRef } from 'react'
import { CircleCheck, MessageCircle, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { createId } from '@/lib/clinic/ids'
import { fillTemplate } from '@/lib/clinic/whatsapp'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { TEMPLATE_PLACEHOLDERS } from '@/constants/clinic'

interface TemplateEditorProps {
    templates: MessageTemplate[]
    defaultTemplateId: string
    onChange: (templates: MessageTemplate[], defaultTemplateId: string) => void
    sampleValues: TemplateValues
    errors?: Record<string, string>
}

const TemplateEditor: FC<TemplateEditorProps> = ({ templates, defaultTemplateId, onChange, sampleValues, errors = {} }): ReactNode => {
    const textareas = useRef<Record<string, HTMLTextAreaElement | null>>({})

    const updateTemplate = (id: string, patch: Partial<MessageTemplate>) => {
        onChange(templates.map(template => (template.id === id ? { ...template, ...patch } : template)), defaultTemplateId)
    }

    const insertToken = (template: MessageTemplate, token: string) => {
        const element = textareas.current[template.id]
        const start = element?.selectionStart ?? template.body.length
        const end = element?.selectionEnd ?? template.body.length
        const body = `${template.body.slice(0, start)}${token}${template.body.slice(end)}`

        updateTemplate(template.id, { body })

        requestAnimationFrame(() => {
            const target = textareas.current[template.id]
            if (!target) return

            target.focus()
            target.setSelectionRange(start + token.length, start + token.length)
        })
    }

    const addTemplate = () => {
        const template: MessageTemplate = {
            id: createId(),
            name: 'New message',
            body: 'Hello [Patient Name], this is [Clinic Name]. '
        }

        onChange([...templates, template], defaultTemplateId || template.id)
    }

    const removeTemplate = (id: string) => {
        const next = templates.filter(template => template.id !== id)
        const nextDefault = defaultTemplateId === id ? next[0]?.id ?? '' : defaultTemplateId

        onChange(next, nextDefault)
    }

    return (
        <div className='flex flex-col gap-4'>
            {templates.map(template => {
                const isDefault = template.id === defaultTemplateId

                return (
                    <article
                        key={template.id}
                        className={cn('rounded-2xl border bg-card p-4 shadow-xs sm:p-5', isDefault ? 'border-primary/40 ring-1 ring-primary/20' : 'border-border')}
                    >
                        <div className='flex flex-wrap items-start justify-between gap-3'>
                            <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                                <label htmlFor={`template-name-${template.id}`} className='text-[13px] font-medium text-foreground'>
                                    Template name
                                </label>

                                <Input
                                    id={`template-name-${template.id}`}
                                    value={template.name}
                                    onChange={event => updateTemplate(template.id, { name: event.target.value })}
                                    className='max-w-sm'
                                    aria-invalid={Boolean(errors[`${template.id}-name`]) || undefined}
                                />

                                {errors[`${template.id}-name`] && (
                                    <p className='text-xs font-medium text-destructive'>{errors[`${template.id}-name`]}</p>
                                )}
                            </div>

                            <div className='flex items-center gap-1.5 pt-6'>
                                {isDefault ? (
                                    <span className='inline-flex h-8 items-center gap-1.5 rounded-lg bg-accent px-2.5 text-xs font-semibold text-accent-foreground'>
                                        <CircleCheck className='size-3.5' />
                                        Default
                                    </span>
                                ) : (
                                    <Button type='button' size='sm' variant='outline' onClick={() => onChange(templates, template.id)}>
                                        Set as default
                                    </Button>
                                )}

                                <Button
                                    type='button'
                                    size='icon-sm'
                                    variant='ghost'
                                    aria-label={`Delete ${template.name}`}
                                    disabled={templates.length <= 1}
                                    className='text-muted-foreground hover:text-destructive'
                                    onClick={() => removeTemplate(template.id)}
                                >
                                    <Trash2 />
                                </Button>
                            </div>
                        </div>

                        <div className='mt-4 grid gap-4 lg:grid-cols-2'>
                            <div className='flex flex-col gap-1.5'>
                                <label htmlFor={`template-body-${template.id}`} className='text-[13px] font-medium text-foreground'>
                                    Message
                                </label>

                                <Textarea
                                    id={`template-body-${template.id}`}
                                    ref={element => {
                                        textareas.current[template.id] = element
                                    }}
                                    rows={5}
                                    value={template.body}
                                    onChange={event => updateTemplate(template.id, { body: event.target.value })}
                                    className='min-h-32'
                                    aria-invalid={Boolean(errors[`${template.id}-body`]) || undefined}
                                />

                                {errors[`${template.id}-body`] && (
                                    <p className='text-xs font-medium text-destructive'>{errors[`${template.id}-body`]}</p>
                                )}

                                <div className='flex flex-wrap gap-1.5'>
                                    <span className='mr-0.5 text-xs text-muted-foreground'>Insert:</span>

                                    {TEMPLATE_PLACEHOLDERS.map(placeholder => (
                                        <button
                                            key={placeholder.token}
                                            type='button'
                                            title={placeholder.description}
                                            onClick={() => insertToken(template, placeholder.token)}
                                            className='rounded-md border border-dashed border-border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground outline-none hover:border-primary/50 hover:text-primary focus-visible:ring-3 focus-visible:ring-ring/40'
                                        >
                                            {placeholder.token}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className='flex flex-col gap-1.5'>
                                <p className='flex items-center gap-1.5 text-[13px] font-medium text-foreground'>
                                    <MessageCircle className='size-4 text-whatsapp' />
                                    Preview with a sample patient
                                </p>

                                <div className='flex-1 rounded-xl bg-[#e7f0ec] p-3'>
                                    <p className='ml-auto max-w-[95%] rounded-xl rounded-tr-sm bg-[#d9fdd3] px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap text-[#111b21] shadow-sm'>
                                        {fillTemplate(template.body, sampleValues) || 'Your message will appear here.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </article>
                )
            })}

            <Button type='button' variant='outline' className='self-start' onClick={addTemplate}>
                <Plus />
                Add message template
            </Button>
        </div>
    )
}

export default TemplateEditor
